import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  registerProject, getProjectRegistry,
  getProjectChecklist, recheckProject, updateProject, deleteProject,
  uploadProjectDocument, getProjectDocuments, deleteProjectDocument,
  getProjectManagersForDropdown,
} from "../api/guru";
import AccountMenu from "../components/AccountMenu";
import { useAuth } from "../context/AuthContext";
import guruLogo from "../assets/guru-logo.png";
import { useChat } from "../context/ChatContext";
import { useNightlyJob } from "../context/NightlyJobContext";

// Projects — Project Registry & Access Checklist.
//
// Top-level page (Dev, 2026-06-22): this used to be a tab nested inside the
// Admin Panel. Moved out to its own sidebar item — same level as "Back to
// Chat" and "Admin" — with its own route (/projects). The landing view is
// the register form plus a table of every registered project and its
// connector status, so registering a project surfaces its connected/missing
// status immediately without drilling into a single-project detail page.

const CONNECTOR_LABEL = {
  sheets: "Google Sheets",
  gsc:    "Search Console",
  ga4:    "GA4",
  karma1: "Karma 1.0",
  karma2: "Karma 2.0",
  gbp:    "Business Profile",
};

const CONNECTOR_SHORT = {
  sheets: "Sheets",
  gsc:    "GSC",
  ga4:    "GA4",
  karma1: "K1",
  karma2: "K2",
  gbp:    "GBP",
};

const STATUS_LABEL = {
  not_requested: "Not Requested",
  requested:     "Pending",
  verified:      "Verified",
  missing:       "Missing",
  revoked:       "Revoked",
};

// Project Documents (Dev, 2026-06-25) — Onboarding Documents, Client
// Requirements, and Minutes of Meeting, scoped to one project so chat can
// boost them for that project specifically. See app/services/projects/documents.py.
const DOC_TYPE_LABEL = {
  onboarding:   "Onboarding Document",
  requirements: "Client Requirements",
  mom:          "Minutes of Meeting",
  other:        "Other",
};

const fmt = {
  datetime: (iso) => iso ? new Date(iso).toLocaleString() : "—",
};

// Project URL link helper (Dev, 2026-06-26) — gsc_site_url doubles as
// "Project URL" once project_type is 'gmb' (same field/column, just
// relabeled — see RegisterBody's docstring in app/api/routes/projects.py).
// Turns either form into a real href: GSC's "sc-domain:example.com"
// syntax, a bare domain, or an already-complete http(s) URL.
const toHref = (url) => {
  if (!url) return "#";
  const trimmed = url.trim();
  if (trimmed.toLowerCase().startsWith("sc-domain:")) {
    return `https://${trimmed.slice("sc-domain:".length)}`;
  }
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

function StatusBadge({ status }) {
  return <span className={"pstatus-badge pstatus-" + status}>{STATUS_LABEL[status] || status}</span>;
}

function ProjectsPanel({ showAlert }) {
  const [view, setView]               = useState("list"); // list | gaps | detail | edit
  // Project type tab (Dev, 2026-06-26, fourth pass) — first tab "SEO /
  // AI-SEO" (the original five-connector registry, unchanged), second tab
  // "GMB" (Google My Business — Karma 1.0 only). Drives which subset of
  // `registry` is visible in the table/gaps list below (see visibleRegistry
  // below). Registering a new project now has its own independent dropdown
  // (registerProjectType below) — switching this tab no longer touches the
  // register form.
  const [projectTypeTab, setProjectTypeTab] = useState("seo"); // 'seo' | 'gmb' | 'seo_gmb' — Dev, 2026-06-27: third tab added, see matchesTypeTab below
  const [registry, setRegistry]       = useState([]);
  const [loading, setLoading]         = useState(true);
  // Register form's own project-type dropdown (Dev, 2026-06-26, fourth
  // pass) — separate from projectTypeTab above. Picking a type here decides
  // which fields the register form shows (GSC Site URL + GA4 for SEO /
  // AI-SEO, Project URL only for GMB) and which type the new project is
  // created as; it no longer follows whichever table tab happens to be
  // selected.
  const [registerProjectType, setRegisterProjectType] = useState("seo"); // 'seo' | 'gmb'
  const [registerName, setRegisterName] = useState("");
  const [registerGa4, setRegisterGa4] = useState("");
  const [registerManagerName, setRegisterManagerName] = useState("");
  const [registerManagerEmail, setRegisterManagerEmail] = useState("");
  const [registerGscSiteUrl, setRegisterGscSiteUrl] = useState("");
  // Executives (Dev, 2026-06-25) — zero or more additional people who help
  // handle the project. Optional at registration (manager alone is
  // sufficient); never emailed, see api/guru.js's registerProject() doc.
  const [registerExecutives, setRegisterExecutives] = useState([{ name: "", email: "" }]);
  // aliases (Dev, 2026-07-07) — optional comma-separated short-names typed by
  // the project manager (e.g. "MIL, TMS"). Stored as a single text string in
  // the input; split into a list only when submitted.
  const [registerAliases, setRegisterAliases] = useState("");
  const [registering, setRegistering] = useState(false);
  // Nightly job "running" state lives in NightlyJobContext now, not here
  // (Dev, 2026-07-03) — see that file's comment: it used to be local
  // useState, so navigating away from Projects and back reset the button
  // even though the job was still (or had already finished) running on the
  // backend the whole time.
  const { running: runningJob, lastResult: nightlyJobResult, runNow: handleRunNightlyJob } = useNightlyJob();
  const seenNightlyResultAt = useRef(null);

  // Search (Dev, 2026-06-26) — live, case-insensitive filter across the
  // currently visible tab's projects: project name, manager name/email,
  // every executive's name/email, and the project's URL (GSC Site URL on
  // the SEO tab, Project URL on the GMB tab — same field). See
  // visibleRegistry below, which every list/table on this page reads from.
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedKey, setSelectedKey] = useState(null);
  const [checklist, setChecklist]     = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [rechecking, setRechecking]   = useState(false);

  // Edit — moved to its own page (Dev, 2026-06-26, third pass). Editing a
  // project used to happen inline, as editable inputs crammed into the
  // registry table's row. That made labels invisible (placeholder text
  // only) and the row itself wider than the table. Clicking ✏️ now opens a
  // dedicated "Edit Project" view (same level as the list/gaps/detail
  // views below) with every field laid out vertically with a real <label>
  // above each input — editingKey is still the project_key being edited;
  // editFields/editExecutives still hold the in-progress values until
  // Save/Cancel, exactly as before.
  const [editingKey, setEditingKey] = useState(null);
  // projectType (Dev, 2026-06-26) is carried in editFields purely to drive
  // which inputs render/are required here — it's never sent to updateProject()
  // since project_type is immutable post-registration (see saveEdit below).
  const [editFields, setEditFields] = useState({ displayName: "", ga4PropertyId: "", managerName: "", managerEmail: "", gscSiteUrl: "", projectType: "seo" });
  // editExecutives (Dev, 2026-06-25, second pass) — executives are editable
  // right on the Edit Project page too, not just on the detail page.
  // Populated from the project's existing executives in startEdit();
  // defaults to one blank row when it has none, so there's always an empty
  // name/email pair ready to fill in.
  const [editExecutives, setEditExecutives] = useState([{ name: "", email: "" }]);
  // editAliases (Dev, 2026-07-07) — comma-separated alias string for the edit
  // form; populated from the project's existing aliases in startEdit().
  const [editAliases, setEditAliases] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  // Project Documents — Onboarding Docs / Client Requirements / MoM (Dev,
  // 2026-06-25). Dev, 2026-06-26, seventh pass: this stays a fully-featured
  // document ingester (upload + delete) on the view page even though the
  // PROJECT INFO above it is read-only there — "no edit/add/delete" was
  // about the project's own fields (name, manager, URL, executives), not
  // about documents.
  const [documents, setDocuments]       = useState([]);
  const [loadingDocs, setLoadingDocs]   = useState(false);
  const [docFile, setDocFile]           = useState(null);
  const [docType, setDocType]           = useState("onboarding");
  const [docMeetingDate, setDocMeetingDate] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [docDragOver, setDocDragOver]   = useState(false);
  const docFileInput                    = useRef(null);

  const loadRegistry = useCallback(() => {
    setLoading(true);
    getProjectRegistry()
      .then(r => setRegistry(r.data.projects || []))
      .catch(() => showAlert("error", "Could not load the project registry."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  // Project Managers (Dev, 2026-07-03) — populates the "Project manager"
  // dropdown below on both the Register and Edit forms. A Knowledge Admin
  // creates these from the Admin Panel's Project Managers tab; picking one
  // here just fills in manager_name/manager_email exactly like the old
  // free-text inputs did, so registration/edit and the pending-access
  // notification path are unchanged.
  //
  // getProjectManagersForDropdown() (Dev, 2026-07-04) — NOT getProjectManagers().
  // That one hits /admin/project-managers, which is admin-only; a signed-in
  // Project Manager viewing THIS page got a 403 and an empty dropdown, so
  // they couldn't register or edit a project at all (manager selection is
  // required). getProjectManagersForDropdown() hits /projects/managers
  // instead — same data, but on this router (require_admin_or_pm), so it
  // actually works for a Project Manager, not just a Knowledge Admin.
  const [projectManagers, setProjectManagers] = useState([]);
  useEffect(() => {
    getProjectManagersForDropdown()
      .then(r => setProjectManagers(r.data.project_managers || []))
      .catch(() => { /* non-fatal — dropdown just shows empty */ });
  }, []);

  // Executives are optional (Dev, 2026-06-25 decision) — blank rows are
  // dropped on submit rather than required to be filled in or removed.
  const cleanExecutives = (rows) =>
    rows
      .map(x => ({ name: (x.name || "").trim(), email: (x.email || "").trim() }))
      .filter(x => x.name || x.email);

  const addExecutiveRow = (setter, rows) => setter([...rows, { name: "", email: "" }]);
  const removeExecutiveRow = (setter, rows, i) => setter(rows.filter((_, idx) => idx !== i));
  const updateExecutiveRow = (setter, rows, i, field, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: value };
    setter(next);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    // Dev, 2026-06-27: GA4/GSC-Site-URL-labeled fields apply to anything
    // EXCEPT a bare 'gmb' project — i.e. 'seo' and the new 'seo_gmb' type
    // both show/use them, only 'gmb' surfaces the URL field as a plain
    // "Project URL" with no GA4 field at all.
    const hasGa4 = registerProjectType !== "gmb";
    const name = registerName.trim();
    // Dev, 2026-06-26, second pass: Project URL is now required for every
    // type — it's registerGscSiteUrl either way, just labeled "GSC Site
    // URL" on the SEO/AI-SEO + SEO+GMB types and "Project URL" on the bare
    // GMB type (see the conditional input in the register form below).
    const gscSiteUrl = registerGscSiteUrl.trim();
    const managerName = registerManagerName.trim();
    const managerEmail = registerManagerEmail.trim();
    const ga4PropertyId = registerGa4.trim();
    if (!name || !managerName || !managerEmail || !gscSiteUrl || (hasGa4 && !ga4PropertyId)) {
      showAlert("error", hasGa4
        ? "Project name, GSC Site URL, GA4 Property ID, manager name, and manager email id are all required."
        : "Project name, Project URL, manager name, and manager email id are all required.");
      return;
    }
    setRegistering(true);
    try {
      const executives = cleanExecutives(registerExecutives);
      // Split alias string into a clean array (Dev, 2026-07-07)
      const aliasesList = registerAliases.split(",").map(a => a.trim()).filter(Boolean);
      const { data } = await registerProject(
        name,
        hasGa4 ? ga4PropertyId : "",
        managerName,
        managerEmail,
        gscSiteUrl,
        executives.length ? executives : null,
        registerProjectType,
        aliasesList.length ? aliasesList : null,
      );
      showAlert("success", `✅ ${data.display_name || name} registered — ${data.completion}. See its connector status in the table below.`);
      setRegisterName("");
      setRegisterGa4("");
      setRegisterManagerName("");
      setRegisterManagerEmail("");
      setRegisterGscSiteUrl("");
      setRegisterExecutives([{ name: "", email: "" }]);
      setRegisterAliases("");
      // Stay on the list view — the table below already shows every
      // connector's connected/missing status for the project just registered.
      loadRegistry();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Registration failed.");
    } finally {
      setRegistering(false);
    }
  };

  const startEdit = (p) => {
    setEditingKey(p.project_key);
    const ga4Item = (p.connectors || []).find(c => c.connector_type === "ga4");
    setEditFields({
      displayName: p.display_name || "",
      ga4PropertyId: (ga4Item && ga4Item.resource_summary !== "Not on file yet" ? ga4Item.resource_summary : "") || "",
      managerName: p.manager_name || "",
      managerEmail: p.manager_email || "",
      gscSiteUrl: p.gsc_site_url || "",
      projectType: p.project_type || "seo",
    });
    const execs = (p.executives || []).map(x => ({ name: x.name || "", email: x.email || "" }));
    setEditExecutives(execs.length ? execs : [{ name: "", email: "" }]);
    // Populate aliases as comma-separated string (Dev, 2026-07-07)
    setEditAliases((p.aliases || []).join(", "));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditFields({ displayName: "", ga4PropertyId: "", managerName: "", managerEmail: "", gscSiteUrl: "", projectType: "seo" });
    setEditExecutives([{ name: "", email: "" }]);
    setEditAliases("");
  };

  const saveEdit = async (projectKey) => {
    setSavingEdit(true);
    try {
      const executives = cleanExecutives(editExecutives);
      // Dev, 2026-06-27: GA4 applies to anything except a bare 'gmb'
      // project — 'seo' and 'seo_gmb' both have a GA4 row, only 'gmb' does
      // not.
      const hasGa4 = editFields.projectType !== "gmb";
      const { projectType, gscSiteUrl, ga4PropertyId, ...rest } = editFields;
      // Dev, 2026-06-26, second pass: gscSiteUrl ("Project URL" on a bare
      // GMB project, same field/column as "GSC Site URL" on SEO/SEO+GMB) is
      // required and populated for every project type, so it's always sent.
      // ga4PropertyId stays out of the payload entirely for a bare GMB
      // project — it has no GA4 row at all, so that key must be left OUT
      // (not sent as "") for one: updateProject()'s `?? null` mapping in
      // api/guru.js turns an omitted key into "leave unchanged", which is
      // exactly right for a field that doesn't apply to this project.
      // Dev, 2026-06-27: projectType IS now sent — the Edit page's new
      // Project Type selector lets an admin move a project between 'seo' /
      // 'gmb' / 'seo_gmb' (unlike registration, where it's a one-time
      // choice). Backend handles adding any newly-required connector rows.
      // Aliases: always send so the backend can replace whatever's on file
      // (Dev, 2026-07-07). An empty string clears all aliases ([]).
      const aliasesList = editAliases.split(",").map(a => a.trim()).filter(Boolean);
      const fields = hasGa4
        ? { ...rest, gscSiteUrl, ga4PropertyId, executives, projectType, aliases: aliasesList }
        : { ...rest, gscSiteUrl, executives, projectType, aliases: aliasesList };
      await updateProject(projectKey, fields);
      showAlert("success", "✅ Project updated.");
      cancelEdit();
      // Dev, 2026-06-26, third pass: edit now lives on its own page (view
      // "edit") rather than inline in the table, so a successful save
      // returns the user to the list — same place the ✏️ button was clicked
      // from.
      setView("list");
      loadRegistry();
      if (selectedKey === projectKey) openDetail(projectKey);
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Update failed.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.display_name}" and all of its checklist rows? This can't be undone.`)) return;
    setDeletingKey(p.project_key);
    try {
      await deleteProject(p.project_key);
      showAlert("success", `🗑️ ${p.display_name} deleted.`);
      if (selectedKey === p.project_key) { setView("list"); setChecklist(null); setSelectedKey(null); }
      loadRegistry();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Delete failed.");
    } finally {
      setDeletingKey(null);
    }
  };

  const loadDocuments = useCallback((key) => {
    setLoadingDocs(true);
    getProjectDocuments(key)
      .then(r => setDocuments(r.data.documents || []))
      .catch(() => showAlert("error", "Could not load this project's documents."))
      .finally(() => setLoadingDocs(false));
  }, [showAlert]);

  const openDetail = (key) => {
    setSelectedKey(key);
    setView("detail");
    setLoadingChecklist(true);
    getProjectChecklist(key)
      .then(r => setChecklist(r.data))
      .catch(() => showAlert("error", "Could not load that project's checklist."))
      .finally(() => setLoadingChecklist(false));
    setDocFile(null);
    setDocType("onboarding");
    setDocMeetingDate("");
    loadDocuments(key);
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!selectedKey || !docFile) {
      showAlert("error", "Choose a file to upload.");
      return;
    }
    if (docType === "mom" && !docMeetingDate) {
      showAlert("error", "Meeting date is required for Minutes of Meeting.");
      return;
    }
    setUploadingDoc(true);
    try {
      const { data } = await uploadProjectDocument(selectedKey, docFile, docType, docMeetingDate);
      showAlert("success", `✅ ${data.message || "Document uploaded."}`);
      setDocFile(null);
      setDocMeetingDate("");
      loadDocuments(selectedKey);
    } catch (err) {
      // Full diagnostic dump -- open the browser console (F12) on the live
      // site when an upload fails to see the real cause: status code, the
      // backend's error detail (if the request even reached the backend),
      // or a network-level failure (CORS block, timeout, connection refused,
      // 413 from a reverse-proxy body-size limit before FastAPI ever saw it).
      console.error("[Project document upload] failed:", {
        projectKey: selectedKey,
        file: docFile && docFile.name,
        size: docFile && docFile.size,
        docType,
        status: err.response ? err.response.status : "no response (network/CORS/timeout)",
        detail: err.response && err.response.data,
        message: err.message,
        code: err.code,
      });
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Upload failed.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocDrop = (e) => {
    e.preventDefault();
    setDocDragOver(false);
    if (e.dataTransfer.files[0]) setDocFile(e.dataTransfer.files[0]);
  };

  const handleDeleteDocument = async (sourceId) => {
    if (!selectedKey) return;
    if (!window.confirm("Remove this document and its content from the knowledge base?")) return;
    setDeletingDocId(sourceId);
    try {
      await deleteProjectDocument(selectedKey, sourceId);
      showAlert("success", "🗑️ Document removed.");
      loadDocuments(selectedKey);
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Delete failed.");
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleRecheck = async () => {
    if (!selectedKey) return;
    setRechecking(true);
    try {
      const { data } = await recheckProject(selectedKey, checklist && checklist.display_name);
      setChecklist(data);
      // Dev, 2026-07-02: recheck_issues (see api/routes/projects.py) means one
      // or more connectors errored out live and were NOT reconciled — the
      // checklist below may still show stale status for those. Previously
      // this always showed a blanket "Re-check complete", even when a
      // connector's DB write silently failed, which read as "the data is
      // now current" when it wasn't.
      if (data.recheck_issues && data.recheck_issues.length > 0) {
        const names = data.recheck_issues.map(i => i.connector).join(", ");
        showAlert("error", `⚠️ Re-check finished, but ${names} could not be verified live — its status may still be stale. Try again in a moment.`);
      } else {
        showAlert("success", "✅ Re-check complete.");
      }
      loadRegistry();
    } catch {
      showAlert("error", "Re-check failed.");
    } finally {
      setRechecking(false);
    }
  };

  // Surface the nightly job's outcome whenever NightlyJobContext gets a new
  // one — including a run that finished while this page wasn't mounted at
  // all (navigated away and back mid-run). seenNightlyResultAt guards
  // against re-showing the same alert every time this component remounts
  // and sees a result it already displayed once.
  useEffect(() => {
    if (!nightlyJobResult || nightlyJobResult.at === seenNightlyResultAt.current) return;
    seenNightlyResultAt.current = nightlyJobResult.at;
    showAlert(nightlyJobResult.ok ? "success" : "error", nightlyJobResult.message);
    if (nightlyJobResult.ok) loadRegistry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nightlyJobResult]);

  // Dev, 2026-06-26, fourth pass: this tab only changes which project type
  // the table/gaps list below is filtered to — it no longer touches the
  // register form at all (that has its own independent dropdown + reset
  // function right below).
  const switchProjectTypeTab = (type) => {
    setProjectTypeTab(type);
  };

  // Register form's project-type dropdown (Dev, 2026-06-26, fourth pass) —
  // switching it clears the form, since the two types use different
  // required fields (a half-filled GSC Site URL left over from SEO / AI-SEO
  // has no home on the GMB form, and vice versa).
  const switchRegisterProjectType = (type) => {
    setRegisterProjectType(type);
    setRegisterName("");
    setRegisterGa4("");
    setRegisterManagerName("");
    setRegisterManagerEmail("");
    setRegisterGscSiteUrl("");
    setRegisterExecutives([{ name: "", email: "" }]);
  };

  // visibleRegistry (Dev, 2026-06-26) — every registered project filtered
  // down to whichever tab (SEO/AI-SEO vs GMB) is currently selected, AND
  // (second pass, same day) further filtered by the search box. Every
  // list/table/gap-count on this page reads from this, not the raw
  // `registry` state, so "under each tab, only the projects registered
  // under that category, matching the current search" holds everywhere at
  // once.
  const searchNeedle = searchQuery.trim().toLowerCase();
  const matchesSearch = (p) => {
    if (!searchNeedle) return true;
    const haystacks = [
      p.display_name, p.project_key, p.manager_name, p.manager_email, p.gsc_site_url,
      ...(p.executives || []).flatMap(x => [x.name, x.email]),
      // Dev, 2026-07-07: also search aliases so filtering by "MIL" surfaces
      // "Mobility Infotech Logistics" in the table.
      ...(p.aliases || []),
    ];
    return haystacks.some(h => (h || "").toLowerCase().includes(searchNeedle));
  };
  // Dev, 2026-06-27: third tab added for 'seo_gmb' — a project of that type
  // now lives ONLY under the "SEO + GMB" tab, exact-match like the other
  // two. (Superseded the earlier "show seo_gmb under both tabs" approach.)
  const matchesTypeTab = (p) => {
    const t = p.project_type || "seo";
    return t === projectTypeTab;
  };
  const visibleRegistry = registry
    .filter(matchesTypeTab)
    .filter(matchesSearch);

  // Access Gaps — every non-verified connector row, scoped to the currently
  // selected project type tab.
  const gaps = [];
  visibleRegistry.forEach(proj => {
    (proj.connectors || []).forEach(c => {
      if (c.status !== "verified") {
        gaps.push({ project_key: proj.project_key, display_name: proj.display_name, ...c });
      }
    });
  });

  if (view === "detail") {
    return (
      <div className="admin-panel">
        <div className="panel-header">
          <h2>{checklist ? checklist.display_name : selectedKey}</h2>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-secondary btn-sm" onClick={handleRecheck} disabled={rechecking || loadingChecklist}>
              {rechecking ? "⏳ Checking…" : "🔄 Re-check Now"}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setView("list"); setChecklist(null); setSelectedKey(null); }}>
              ← Back to Projects
            </button>
          </div>
        </div>

        {loadingChecklist ? (
          <div className="panel-loading">Loading checklist…</div>
        ) : !checklist || !checklist.found ? (
          <div className="empty-sources">Project not found.</div>
        ) : (
          <>
          <div className="table-scroll">
          <table className="sources-table">
            <thead>
              <tr><th>Connector</th><th>Status</th><th>Detail</th><th>Verified Since</th><th>Last Checked</th></tr>
            </thead>
            <tbody>
              {checklist.connectors.map(c => (
                <tr key={c.connector_type}>
                  <td>{CONNECTOR_LABEL[c.connector_type] || c.connector_type}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>{c.resource_summary}</td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>
                    {c.verified_at ? fmt.datetime(c.verified_at) : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>
                    {c.last_checked_at ? fmt.datetime(c.last_checked_at) : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Project Information (Dev, 2026-06-26, ninth pass) — moved below
              the connector checklist per Dev's request ("below of all
              chekers and above ... project document section"). Read order
              top to bottom is now: connector/access status, then project
              info, then documents. */}
          <div className="admin-card" style={{ marginTop: "24px", marginBottom: "24px", maxWidth: "640px" }}>
            <h3><span>ℹ️</span> Project Information</h3>
            <div className="table-scroll">
              <table className="sources-table">
                <tbody>
                  <tr><td style={{ fontWeight: 600, width: "160px" }}>Project Name</td><td>{checklist.display_name}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Project Type</td><td>{checklist.project_type === "gmb" ? "GMB" : checklist.project_type === "seo_gmb" ? "SEO + GMB" : "SEO / AI-SEO"}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Completion</td><td>{checklist.completion}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Manager Name</td><td>{checklist.manager_name || <span style={{ color: "#d1d5db" }}>—</span>}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Manager Email</td><td>{checklist.manager_email || <span style={{ color: "#d1d5db" }}>—</span>}</td></tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Executives</td>
                    <td>
                      {(checklist.executives || []).length
                        ? checklist.executives.map(x => x.name && x.email ? `${x.name} (${x.email})` : (x.name || x.email)).filter(Boolean).join(", ")
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  {/* Aliases (Dev, 2026-07-07) */}
                  <tr>
                    <td style={{ fontWeight: 600 }}>Aliases</td>
                    <td>
                      {(checklist.aliases || []).length
                        ? (checklist.aliases || []).map(a => (
                            <span key={a} className="alias-chip">{a.toUpperCase()}</span>
                          ))
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Project URL</td>
                    <td>
                      {checklist.gsc_site_url ? (
                        <a className="project-url-link" href={toHref(checklist.gsc_site_url)} target="_blank" rel="noreferrer">
                          {checklist.gsc_site_url}
                        </a>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                  <tr><td style={{ fontWeight: 600 }}>Registered On</td><td>{checklist.registered_on ? fmt.datetime(checklist.registered_on) : <span style={{ color: "#d1d5db" }}>—</span>}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        <div className="admin-card" style={{ marginTop: "24px", maxWidth: "720px" }}>
          <h3><span>📄</span> Documents</h3>
          <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "-4px", marginBottom: "16px" }}>
            Onboarding Documents, Client Requirements, and Minutes of Meeting for this project. Guru
            boosts these above the general knowledge base when a chat question is about this project.
          </p>

          <form onSubmit={handleUploadDocument} style={{ maxWidth: "380px", marginBottom: "22px" }}>
            <div className="form-group">
              <label>Document Type</label>
              <select
                value={docType}
                onChange={e => setDocType(e.target.value)}
                style={{ width: "100%", background: "#f5f6fa", border: "1px solid #d1d5db", borderRadius: "8px", padding: "9px 12px", fontSize: "13px", color: "#1e2235" }}
              >
                {Object.entries(DOC_TYPE_LABEL).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {docType === "mom" && (
              <div className="form-group">
                <label>Meeting Date</label>
                <input
                  type="date"
                  value={docMeetingDate}
                  onChange={e => setDocMeetingDate(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>File</label>
              <div
                className={"drop-zone" + (docDragOver ? " drag-over" : "")}
                onClick={() => docFileInput.current && docFileInput.current.click()}
                onDragOver={e => { e.preventDefault(); setDocDragOver(true); }}
                onDragLeave={() => setDocDragOver(false)}
                onDrop={handleDocDrop}
                style={{ padding: "18px" }}
              >
                <div className="drop-icon">📎</div>
                {docFile ? (
                  <p style={{ color: "#1e2235", fontWeight: 500 }}>{docFile.name}</p>
                ) : (
                  <>
                    <p>Click or drag a file here</p>
                    <p style={{ fontSize: "11px", marginTop: "4px", color: "#6b7280" }}>PDF · Word (.docx) · Excel (.xlsx/.xls) · CSV · Text (.txt)</p>
                  </>
                )}
              </div>
              <input
                ref={docFileInput}
                type="file"
                accept=".pdf,.docx,.xlsx,.xls,.csv,.txt"
                style={{ display: "none" }}
                onChange={e => setDocFile(e.target.files[0] || null)}
              />
            </div>

            <button className="btn btn-primary" disabled={uploadingDoc || !docFile}>
              {uploadingDoc ? "Uploading…" : "Upload Document"}
            </button>
          </form>

          {loadingDocs ? (
            <div className="panel-loading">Loading documents…</div>
          ) : documents.length === 0 ? (
            <div className="empty-sources">No documents attached to this project yet.</div>
          ) : (
            <div className="table-scroll">
            <table className="sources-table">
              <thead>
                <tr><th>Document</th><th>Type</th><th>Meeting Date</th><th>Added</th><th></th></tr>
              </thead>
              <tbody>
                {documents.map(d => (
                  <tr key={d.id}>
                    <td>{d.label || d.filename}</td>
                    <td>{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</td>
                    <td style={{ color: "#6b7280", fontSize: "12px" }}>{d.meeting_date || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                    <td style={{ color: "#6b7280", fontSize: "12px" }}>{fmt.datetime(d.added_on)}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={deletingDocId === d.id}
                        onClick={() => handleDeleteDocument(d.id)}
                      >
                        {deletingDocId === d.id ? "Deleting…" : "🗑️"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Edit Project — its own page (Dev, 2026-06-26, third pass). Replaces the
  // old inline table-row edit: every field gets a real <label> above it
  // (via the same .form-group class the Documents form above already
  // uses), stacked vertically instead of crammed into table cells. Opened
  // via startEdit(p) + setView("edit") from the ✏️ button in the list
  // view's registry table below; Cancel/Save both return to "list".
  if (view === "edit") {
    // Dev, 2026-06-27: GSC Site URL + GA4 fields apply to anything except a
    // bare 'gmb' project — 'seo' and 'seo_gmb' both show them; only 'gmb'
    // shows the plain "Project URL" field with no GA4 field at all.
    const hasGa4 = editFields.projectType !== "gmb";
    return (
      <div className="admin-panel">
        <div className="panel-header">
          <h2>Edit Project <span className="panel-sub">{editFields.displayName || editingKey}</span></h2>
          <button
            className="btn btn-secondary btn-sm"
            disabled={savingEdit}
            onClick={() => { cancelEdit(); setView("list"); }}
          >
            ← Back to Projects
          </button>
        </div>

        <div className="admin-card" style={{ maxWidth: "640px" }}>
          <form
            onSubmit={e => { e.preventDefault(); saveEdit(editingKey); }}
            style={{ display: "flex", flexDirection: "column" }}
          >
            {/* Project Type changer (Dev, 2026-06-27) — unlike the register
                form's dropdown, this one does NOT reset the other fields on
                change: it's reclassifying an existing project, not starting
                a fresh draft. Switching to/from 'gmb' just toggles which of
                GSC Site URL + GA4 vs. plain Project URL show below (hasGa4),
                without touching whatever's already typed into either. */}
            <div className="form-group" style={{ maxWidth: "220px" }}>
              <label>Project Type</label>
              <select
                value={editFields.projectType}
                onChange={e => setEditFields({ ...editFields, projectType: e.target.value })}
                style={{ width: "100%", background: "#f5f6fa", border: "1px solid #d1d5db", borderRadius: "8px", padding: "9px 12px", fontSize: "13px", color: "#1e2235" }}
              >
                <option value="seo">SEO / AI-SEO</option>
                <option value="gmb">GMB</option>
                <option value="seo_gmb">SEO + GMB</option>
              </select>
            </div>

            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={editFields.displayName}
                onChange={e => setEditFields({ ...editFields, displayName: e.target.value })}
                placeholder="Display name"
                required
              />
            </div>

            {hasGa4 ? (
              <>
                <div className="form-group">
                  <label>GSC Site URL</label>
                  <input
                    type="text"
                    value={editFields.gscSiteUrl}
                    onChange={e => setEditFields({ ...editFields, gscSiteUrl: e.target.value })}
                    placeholder="GSC Site URL"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>GA4 Property ID</label>
                  <input
                    type="text"
                    value={editFields.ga4PropertyId}
                    onChange={e => setEditFields({ ...editFields, ga4PropertyId: e.target.value })}
                    placeholder="GA4 Property ID"
                    required
                  />
                </div>
              </>
            ) : (
              // Project URL (Dev, 2026-06-26) — same editFields.gscSiteUrl
              // field as the GSC Site URL input above, just relabeled for a
              // bare GMB project.
              <div className="form-group">
                <label>Project URL</label>
                <input
                  type="text"
                  value={editFields.gscSiteUrl}
                  onChange={e => setEditFields({ ...editFields, gscSiteUrl: e.target.value })}
                  placeholder="Project URL"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Project Manager</label>
              <select
                value={editFields.managerEmail}
                onChange={e => {
                  const email = e.target.value;
                  const pm = projectManagers.find(p => p.email === email);
                  setEditFields({
                    ...editFields,
                    managerEmail: email,
                    managerName: pm ? pm.name : editFields.managerName,
                  });
                }}
                required
              >
                <option value="">
                  {projectManagers.length === 0 ? "No project managers yet — add one in Admin" : "Select project manager"}
                </option>
                {projectManagers.map(p => (
                  <option key={p.email} value={p.email}>{p.name} ({p.email})</option>
                ))}
                {/* Keep whatever manager is already on file selectable even if they've
                    since been removed from the Project Managers list (Dev, 2026-07-03) —
                    otherwise editing an older project would silently blank the field. */}
                {editFields.managerEmail && !projectManagers.some(p => p.email === editFields.managerEmail) && (
                  <option value={editFields.managerEmail}>
                    {editFields.managerName} ({editFields.managerEmail})
                  </option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>Executives <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional, never emailed)</span></label>
              {editExecutives.map((ex, i) => (
                <div key={i} className="register-exec-row" style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                  <input
                    type="text"
                    value={ex.name}
                    onChange={e => updateExecutiveRow(setEditExecutives, editExecutives, i, "name", e.target.value)}
                    placeholder="Executive name"
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <input
                    type="email"
                    value={ex.email}
                    onChange={e => updateExecutiveRow(setEditExecutives, editExecutives, i, "email", e.target.value)}
                    placeholder="Executive email"
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => removeExecutiveRow(setEditExecutives, editExecutives, i)}
                    disabled={editExecutives.length === 1}
                    title="Remove this executive"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => addExecutiveRow(setEditExecutives, editExecutives)}
              >
                + Add Executive
              </button>
            </div>

            {/* Aliases (Dev, 2026-07-07) */}
            <div className="form-group">
              <label>
                Aliases <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={editAliases}
                onChange={e => setEditAliases(e.target.value)}
                placeholder="Comma-separated short names, e.g. MIL, TMS"
              />
              <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px", marginBottom: 0 }}>
                Users can type any of these short names in chat and Guru will search as if they typed the full project name.
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
              <button
                className="btn btn-primary"
                style={{ width: "auto", padding: "9px 24px" }}
                disabled={
                  savingEdit ||
                  !editFields.displayName.trim() ||
                  !editFields.managerName.trim() ||
                  !editFields.managerEmail.trim() ||
                  !editFields.gscSiteUrl.trim() ||
                  (editFields.projectType !== "gmb" && !editFields.ga4PropertyId.trim())
                }
              >
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={savingEdit}
                onClick={() => { cancelEdit(); setView("list"); }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Projects <span className="panel-sub">
          {projectTypeTab === "gmb"
            ? "Google Business Profile — access tracked via Karma 1.0 + Business Profile (live check)"
            : projectTypeTab === "seo_gmb"
            ? "SEO + GMB — every connector: Sheets, Search Console, GA4, Karma 1.0 & 2.0, and Business Profile (live check)"
            : "access registry across Sheets, Search Console, GA4, Karma 1.0 & 2.0"}
        </span></h2>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleRunNightlyJob}
            disabled={runningJob}
            title="Manually run the 10am drift-check + pending-digest job now (QA)"
          >
            {runningJob ? "⏳ Running…" : "🕙 Run Nightly Job Now"}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadRegistry}>↻ Refresh</button>
        </div>
      </div>

      {/* All Projects / Access Gaps (Dev, 2026-06-26, fifth pass) — moved
          back to the top, directly under the header, same as before the
          register card was introduced up here. */}
      <div className="filter-tabs">
        <button className={"filter-tab" + (view === "list" ? " active" : "")} onClick={() => setView("list")}>
          All Projects
        </button>
        <button className={"filter-tab" + (view === "gaps" ? " active" : "")} onClick={() => setView("gaps")}>
          ⚠️ Access Gaps{gaps.length > 0 ? ` (${gaps.length})` : ""}
        </button>
      </div>

      {/* Register New Project (Dev, 2026-06-26, fourth pass) — given its
          own Project Type dropdown, independent of which table tab is
          selected below. Picking a type here swaps the required fields
          (GSC Site URL + GA4 for SEO / AI-SEO, Project URL only for GMB).
          List view only — registering a project while looking at Access
          Gaps doesn't make as much sense. */}
      {view === "list" && (
        <div className="admin-card" style={{ marginBottom: "20px", maxWidth: "640px" }}>
          <h3><span>➕</span> Register a {registerProjectType === "gmb" ? "GMB" : registerProjectType === "seo_gmb" ? "SEO + GMB" : ""} Project</h3>
          <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div className="form-group" style={{ maxWidth: "220px" }}>
              <label>Project Type</label>
              <select
                value={registerProjectType}
                onChange={e => switchRegisterProjectType(e.target.value)}
                style={{ width: "100%", background: "#f5f6fa", border: "1px solid #d1d5db", borderRadius: "8px", padding: "9px 12px", fontSize: "13px", color: "#1e2235" }}
              >
                <option value="seo">SEO / AI-SEO</option>
                <option value="gmb">GMB</option>
                <option value="seo_gmb">SEO + GMB</option>
              </select>
            </div>

            <div className="register-form-row" style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                value={registerName}
                onChange={e => setRegisterName(e.target.value)}
                placeholder="Project name (required)"
                required
                style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
              />
              {registerProjectType !== "gmb" ? (
                // Dev, 2026-06-27: both 'seo' and 'seo_gmb' show the GSC
                // Site URL + GA4 fields — only a bare 'gmb' project gets the
                // plain "Project URL" field below instead.
                <>
                  <input
                    type="text"
                    value={registerGscSiteUrl}
                    onChange={e => setRegisterGscSiteUrl(e.target.value)}
                    placeholder="GSC Site URL (required)"
                    required
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <input
                    type="text"
                    value={registerGa4}
                    onChange={e => setRegisterGa4(e.target.value)}
                    placeholder="GA4 Property ID (required)"
                    required
                    className="register-ga4-input"
                    style={{ width: "190px", padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                </>
              ) : (
                // Project URL (Dev, 2026-06-26) — same registerGscSiteUrl
                // state as the SEO / AI-SEO field above, just relabeled.
                <input
                  type="text"
                  value={registerGscSiteUrl}
                  onChange={e => setRegisterGscSiteUrl(e.target.value)}
                  placeholder="Project URL (required)"
                  required
                  style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                />
              )}
            </div>
            <div className="register-actions-row" style={{ display: "flex", gap: "8px" }}>
              <select
                value={registerManagerEmail}
                onChange={e => {
                  const email = e.target.value;
                  const pm = projectManagers.find(p => p.email === email);
                  setRegisterManagerEmail(email);
                  setRegisterManagerName(pm ? pm.name : "");
                }}
                required
                style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
              >
                <option value="">
                  {projectManagers.length === 0 ? "No project managers yet — add one in Admin" : "Select project manager (required)"}
                </option>
                {projectManagers.map(p => (
                  <option key={p.email} value={p.email}>{p.name} ({p.email})</option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm register-submit-btn"
                disabled={
                  registering ||
                  !registerName.trim() ||
                  !registerManagerName.trim() ||
                  !registerManagerEmail.trim() ||
                  !registerGscSiteUrl.trim() ||
                  (registerProjectType !== "gmb" && !registerGa4.trim())
                }
              >
                {registering ? "Registering…" : "Register"}
              </button>
            </div>

            <div style={{ marginTop: "4px" }}>
              <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>
                Executives (optional) — additional people who help handle this project; not emailed
              </label>
              {registerExecutives.map((ex, i) => (
                <div key={i} className="register-exec-row" style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                  <input
                    type="text"
                    value={ex.name}
                    onChange={e => updateExecutiveRow(setRegisterExecutives, registerExecutives, i, "name", e.target.value)}
                    placeholder="Executive name"
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <input
                    type="email"
                    value={ex.email}
                    onChange={e => updateExecutiveRow(setRegisterExecutives, registerExecutives, i, "email", e.target.value)}
                    placeholder="Executive email"
                    style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => removeExecutiveRow(setRegisterExecutives, registerExecutives, i)}
                    disabled={registerExecutives.length === 1}
                    title="Remove this executive"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => addExecutiveRow(setRegisterExecutives, registerExecutives)}
              >
                + Add Executive
              </button>
            </div>

            {/* Aliases (Dev, 2026-07-07) */}
            <div style={{ marginTop: "8px" }}>
              <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>
                Aliases (optional) — comma-separated short names users may type instead of the full project name (e.g. <em>MIL, TMS</em>)
              </label>
              <input
                type="text"
                className="form-input"
                value={registerAliases}
                onChange={e => setRegisterAliases(e.target.value)}
                placeholder="e.g. MIL, TMS (comma-separated)"
              />
            </div>
          </form>
          <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "8px" }}>
            {registerProjectType === "gmb"
              ? "Register a GMB project here first — Guru is the source of truth. GMB projects don't request GA4, Search Console, Karma 2.0, or Google Sheets access at all; Karma 1.0 (backlinks) and Business Profile ('gbp', a live check against Google's Business Profile API) are checked instead. Name, Project URL, manager name, and manager email id are all required; the manager is emailed along with Knowledge Admins whenever this project has pending access items. Executives are optional and are never emailed."
              : registerProjectType === "seo_gmb"
              ? "Register a project that is BOTH SEO / AI-SEO and GMB here first — Guru is the source of truth. It gets every connector from both: Sheets, Search Console, GA4, Karma 1.0, Karma 2.0, and Business Profile ('gbp', a live check against Google's Business Profile API). Name, GSC Site URL, manager name, and manager email id are all required; we'll check live whether our service account actually has access to the GSC Site URL you give us, and the manager is emailed along with Knowledge Admins whenever this project has pending access items. Executives are optional and are never emailed."
              : "Register a project here first — Guru is the source of truth. Once it's registered, add it in Karma 1.0, Karma 2.0, Search Console, GA4, etc. and the checklist below will pick it up. Name, GSC Site URL, manager name, and manager email id are all required; we'll check live whether our service account actually has access to the GSC Site URL you give us, and the manager is emailed along with Knowledge Admins whenever this project has pending access items. Executives are optional and are never emailed."}
          </p>
        </div>
      )}

      {/* Dev, 2026-06-26, fifth pass: SEO/AI-SEO vs GMB type tab,
          immediately above the search box (type tab just before the
          search bar, search bar just before the project list — per the
          spec). */}
      <div className="filter-tabs">
        <button className={"filter-tab" + (projectTypeTab === "seo" ? " active" : "")} onClick={() => switchProjectTypeTab("seo")}>
          SEO / AI-SEO
        </button>
        <button className={"filter-tab" + (projectTypeTab === "gmb" ? " active" : "")} onClick={() => switchProjectTypeTab("gmb")}>
          GMB
        </button>
        <button className={"filter-tab" + (projectTypeTab === "seo_gmb" ? " active" : "")} onClick={() => switchProjectTypeTab("seo_gmb")}>
          SEO + GMB
        </button>
      </div>

      {/* Search (Dev, 2026-06-26) — filters visibleRegistry above, so it
          applies to both the list and gaps views, scoped to whichever
          project-type tab is currently selected. Sits directly above the
          project list/gaps table — nothing in between. */}
      <div className="search-box">
        <span className="search-icon">🔎</span>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by project, manager, executive, email, or URL…"
        />
        {searchQuery && (
          <button type="button" className="search-clear" onClick={() => setSearchQuery("")} title="Clear search">
            ✕
          </button>
        )}
      </div>

      {view === "list" && (
        loading ? (
          <div className="panel-loading">Loading projects…</div>
        ) : visibleRegistry.length === 0 ? (
          <div className="empty-sources">
            {projectTypeTab === "gmb"
              ? "No GMB projects registered yet. Register one above to start tracking access."
              : projectTypeTab === "seo_gmb"
              ? "No SEO + GMB projects registered yet. Register one above to start tracking access."
              : "No SEO / AI-SEO projects registered yet. Register one above to start tracking access."}
          </div>
        ) : (
          <div className="table-scroll">
          <table className="sources-table">
            <thead>
              <tr><th>Project</th><th>Manager</th><th>Executives</th><th>Completion</th><th>Connectors</th><th></th></tr>
            </thead>
            <tbody>
              {visibleRegistry.map(p => (
                <tr key={p.project_key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.display_name}</div>
                    {/* Project URL (Dev, 2026-06-26, sixth pass) — moved out of
                        its own column and into a sub-line under the project
                        name, since it's really an attribute of the project,
                        not a peer-level column. */}
                    {p.gsc_site_url && (
                      <a
                        className="project-url-link"
                        href={toHref(p.gsc_site_url)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: "block", fontSize: "11px", marginTop: "2px" }}
                      >
                        {p.gsc_site_url}
                      </a>
                    )}
                  </td>
                  <td className="proj-secondary-cell">
                    {p.manager_name || p.manager_email
                      ? <>{p.manager_name}{p.manager_name && p.manager_email ? <br /> : null}{p.manager_email}</>
                      : <span className="proj-empty">—</span>}
                  </td>
                  <td className="proj-secondary-cell">
                    {(p.executives || []).length
                      ? p.executives.map(x => x.name || x.email).filter(Boolean).join(", ")
                      : <span className="proj-empty">—</span>}
                  </td>
                  <td>{p.completion}</td>
                  <td>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {(p.connectors || []).map(c => (
                        <span
                          key={c.connector_type}
                          className={"pstatus-badge pstatus-" + c.status}
                          title={(CONNECTOR_LABEL[c.connector_type] || c.connector_type) + ": " + (STATUS_LABEL[c.status] || c.status)}
                        >
                          {CONNECTOR_SHORT[c.connector_type] || c.connector_type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openDetail(p.project_key)}>View</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { startEdit(p); setView("edit"); }}>✏️</button>
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={deletingKey === p.project_key}
                        onClick={() => handleDelete(p)}
                      >
                        {deletingKey === p.project_key ? "Deleting…" : "🗑️"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}

      {view === "gaps" && (
        gaps.length === 0 ? (
          <div className="empty-sources">No access gaps — every connector is verified across all registered projects. 🎉</div>
        ) : (
          <div className="table-scroll">
          <table className="sources-table">
            <thead>
              <tr><th>Project</th><th>Connector</th><th>Status</th><th>Detail</th><th>Verified Since</th><th>Last Checked</th><th></th></tr>
            </thead>
            <tbody>
              {gaps.map((g, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{g.display_name}</td>
                  <td>{CONNECTOR_LABEL[g.connector_type] || g.connector_type}</td>
                  <td><StatusBadge status={g.status} /></td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>{g.resource_summary}</td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>
                    {g.verified_at ? fmt.datetime(g.verified_at) : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>
                    {g.last_checked_at ? fmt.datetime(g.last_checked_at) : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => openDetail(g.project_key)}>View →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}

      <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
        Pending items trigger an email to all Knowledge Admins right after registration, then again every
        morning at 10am until resolved. A nightly check also flags any connector that drifts from Verified
        to Missing/Revoked.
      </p>
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const { darkTheme, setDarkTheme } = useChat();
  const [alert, setAlert] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile/tablet off-canvas drawer (Dev, responsive pass)

  const showAlert = useCallback((type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 6000);
  }, []);

  return (
    <div className="chat-page">
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`gpt-sidebar${sidebarOpen ? ' gpt-sidebar-open' : ''}`}>
        <div className="gpt-sidebar-brand">
          <img src={guruLogo} alt="Guru" className="gpt-brand-logo" />
          <div>
            <div className="gpt-brand-name">Guru</div>
            <div className="gpt-brand-sub">SEO Knowledge Agent</div>
          </div>
        </div>
        <div className="gpt-sidebar-topnav">
          <NavLink to="/chat"     className="gpt-nav-link"><span>💬</span> Back to Chat</NavLink>
          {user?.is_admin && (
            <NavLink to="/admin"         className="gpt-nav-link"><span>⚙️</span> Admin</NavLink>
          )}
          <NavLink to="/projects"      className="gpt-nav-link active-nav"><span>📋</span> Projects</NavLink>
          {user?.is_admin && (
            <>
              <NavLink to="/notifications" className="gpt-nav-link"><span>📣</span> Notifications</NavLink>
              <NavLink to="/query-history" className="gpt-nav-link"><span>🔍</span> Query History</NavLink>
            </>
          )}
        </div>

        <div className="gpt-sidebar-spacer" />
        <div style={{ padding: '8px 16px', flexShrink: 0 }}>
          <button className="theme-toggle-btn" style={{ width: '100%', justifyContent: 'flex-start', gap: '8px' }} onClick={() => setDarkTheme(d => !d)} title={darkTheme ? 'Switch to light theme' : 'Switch to dark theme'}>
            {darkTheme ? '☀️ Light mode' : '🌙 Dark mode'}
          </button>
        </div>
        <AccountMenu style={{ marginTop: '0' }} />
      </div>

      <div className="admin-page">
        <div className="mobile-page-header">
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >☰</button>
          <h1>Project Registry</h1>
        </div>
        <p className="subtitle">Register a project and track its connector access — Sheets, Search Console, GA4, Karma 1.0 &amp; 2.0.</p>

        {alert && <div className={"alert alert-" + alert.type}>{alert.msg}</div>}

        <ProjectsPanel showAlert={showAlert} />
      </div>
    </div>
  );
}
